<script setup>
// §8.6 RampBar — stacked, magnitude-ordered, 2px --surface separators (§6.3).
// A single-hue ramp is legal ONLY because colour is not the carrier: every
// segment is labelled in the legend, bars carry the separator, and position
// encodes rank. A RampBar without its RampLegend is NON-CONFORMANT (§6.3) —
// pass :with-legend or render LRampLegend alongside.
import { computed } from 'vue'
import { buildRamp } from './ramp.js'
import LRampLegend from './RampLegend.vue'

const props = defineProps({
	segments: { type: Array, required: true }, // [{ label, value }]
	noun: { type: String, default: 'items' },
	format: { type: Function, default: (v) => String(v) },
	withLegend: { type: Boolean, default: true },
})

const ramp = computed(() => buildRamp(props.segments, { noun: props.noun }))
const pct = (v) => (ramp.value.total ? (v / ramp.value.total) * 100 : 0)
</script>

<template>
	<div class="l-ramp">
		<div class="bar" role="img"
			:aria-label="`Breakdown by magnitude: ${ramp.steps.map((s) => `${s.label} ${format(s.value)}`).join(', ')}`">
			<div v-for="(s, i) in ramp.steps" :key="i" class="seg no-tooltip-icon"
				:style="{ width: pct(s.value) + '%', background: s.color }"
				:title="`${s.label} — ${format(s.value)}`" />
		</div>
		<LRampLegend v-if="withLegend" :steps="ramp.steps" :total="ramp.total" :format="format" />
	</div>
</template>

<style scoped>
.l-ramp
{
	display: flex;
	flex-direction: column;
	gap: var(--sp-4);
}

.bar
{
	display: flex;
	height: 30px;
	border-radius: var(--r-sm);
	overflow: hidden;
	/* the 2px separator between segments (§6.3) */
	gap: var(--ramp-gap);
	background: var(--surface);
}

.seg
{
	height: 100%;
	min-width: 3px;
	transition: width var(--t-fast) var(--ease);
}

.seg:first-child
{
	border-radius: var(--r-sm) 0 0 var(--r-sm);
}

.seg:last-child
{
	border-radius: 0 var(--r-sm) var(--r-sm) 0;
}
</style>
